import { useState, useEffect, useCallback } from "react";
import { Button, Input, notification, Modal } from "antd";
import { SafetyOutlined, CopyOutlined } from "@ant-design/icons";
import OtpCodeInput from "../OtpCodeInput/OtpCodeInput";
import { withLocale } from "../../l10n/index.jsx";
import Loading from "../../utils/loading";
import service from "../../services/Service.js";
import "./TwoFactorCard.scss";

const TwoFactorCard = ({ lang }) => {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [setupData, setSetupData] = useState(null); // { qr, otpauth, secret }
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    const res = await service.get('users/2fa/status');
    if (res?.data?.ok) {
      setEnabled(!!res.data.data.enabled);
    }
    setLoading(false);
  }, []);

  useEffect(() => { 
    loadStatus(); 
  }, [loadStatus]);

  const startSetup = async () => {
    const res = await service.post('users/2fa/setup');
    if (!res?.data?.ok) {
      notification["error"]({ message: lang.handle("Error al obtener datos") });
      return;
    }
    setSetupData(res.data.data);
  };

  const copyRecoveryCodes = useCallback((codes) => {
    const text = codes.join("\n");
    navigator.clipboard.writeText(text).then(() => {
      notification.success({
        message: lang.handle("Códigos copiados al portapapeles"),
      });
    }).catch(err => {
      console.error(err);
      notification.error({
        message: lang.handle("Error al copiar códigos"),
      });
    });
  }, [lang]);

  const showRecoveryCodes = (codes) => {
    Modal.info({
      title: lang.handle("Códigos de recuperación"),
      width: 480,
      okText: lang.handle("Aceptar"),
      content: (
        <div>
          <p className="twofa-codes__intro">
            {lang.handle("Guárdelos en un lugar seguro. No se volverán a mostrar. Cada uno sirve una sola vez si pierde el acceso a su aplicación.")}
          </p>
          <div className="twofa-codes__grid">
            {codes.map((c) => (
              <span key={c} className="twofa-codes__code">{c}</span>
            ))}
          </div>
          <div className="twofa-codes__actions">
            <Button icon={<CopyOutlined />} onClick={() => copyRecoveryCodes(codes)}>
              {lang.handle("Copiar códigos")}
            </Button>
          </div>
        </div>
      ),
    });
  };

const confirmEnable = async (value) => {
    if (!value) {
      notification["error"]({ message: lang.handle("Introduzca el código") });
      return;
    }

    const res = await service.post('users/2fa/enable', { code: value });

    if (!res?.data?.ok) {
      setCode("");
      notification["error"]({ message: lang.handle("Código incorrecto") });
      return;
    }

    setSetupData(null);
    setCode("");
    setEnabled(true);
    showRecoveryCodes(res.data.data.recoveryCodes);
  };

const disable = async (value) => {
    if (!value) {
      notification["error"]({ message: lang.handle("Introduzca el código") });
      return;
    }

    const res = await service.post('users/2fa/disable', {
      code: value,
      type: useRecovery ? 'recovery' : 'totp',
    });

    if (!res?.data?.ok) {
      setCode("");
      notification["error"]({ message: lang.handle("Código incorrecto") });
      return;
    }

    setCode("");
    setUseRecovery(false);
    setEnabled(false);
    notification["success"]({ message: lang.handle("Verificación en dos pasos desactivada") });
  };

  return (
    <div className="twofa">
      {loading && <Loading loading={loading} />}
      <div className="twofa__header">
        <span className="twofa__icon"><SafetyOutlined /></span>
        <h4 className="twofa__title">{lang.handle("Verificación en dos pasos")}</h4>
        <span className={`twofa__badge ${enabled ? "twofa__badge--on" : "twofa__badge--off"}`}>
          {enabled ? lang.handle("Activada") : lang.handle("Desactivada")}
        </span>
      </div>

      <hr className="twofa__divider" />

{enabled && (
        <div>
          <p className="twofa__text">
            {useRecovery
              ? lang.handle("Introduzca uno de sus códigos de recuperación para desactivar la verificación en dos pasos.")
              : lang.handle("Su cuenta está protegida con un segundo factor. Para desactivarlo, introduzca un código de su aplicación de autenticación.")}
          </p>

          {useRecovery ? (
            <>
              <div className="twofa__field">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={lang.handle("Código de recuperación")}
                />
              </div>
              <div className="twofa__actions">
                <Button danger type="primary" onClick={() => disable(code)}>
                  {lang.handle("Desactivar 2FA")}
                </Button>
              </div>
            </>
          ) : (
            <div className="twofa__otp">
              <OtpCodeInput
                value={code}
                onChange={setCode}
                onComplete={disable}
              />
            </div>
          )}

          <div className="twofa__actions">
            <Button
              type="link"
              onClick={() => { setUseRecovery(!useRecovery); setCode(""); }}
            >
              {useRecovery
                ? lang.handle("Usar el código de la aplicación")
                : lang.handle("¿No tiene acceso a la app? Usar un código de recuperación")}
            </Button>
          </div>
        </div>
      )}

      {!enabled && !setupData && (
        <div>
          <p className="twofa__text">
            {lang.handle("Añade una capa extra de seguridad. Necesitará una app como Google Authenticator para generar los códigos.")}
          </p>
          <div className="twofa__actions">
            <Button type="primary" icon={<SafetyOutlined />} onClick={startSetup}>
              {lang.handle("Activar")}
            </Button>
          </div>
        </div>
      )}

      {!enabled && setupData && (
        <div>
          <span className="twofa__step">
            {lang.handle("1. Escanee el código QR con su aplicación de autenticación")}
          </span>
          <div className="twofa__qr">
            <img className="twofa__qr-img" src={setupData.qr} alt="QR 2FA" />
          </div>

          <span className="twofa__step">
            {lang.handle("¿No puede escanear? Introduzca esta clave manualmente")}
          </span>
          <div className="twofa__secret">
            <code>{setupData.secret}</code>
          </div>

          <span className="twofa__step">
            {lang.handle("2. Introduzca el código de 6 dígitos que muestra la app")}
          </span>
          <div className="twofa__otp">
            <OtpCodeInput
              value={code}
              onChange={setCode}
              onComplete={confirmEnable}
            />
          </div>

          <div className="twofa__actions">
            <Button type="primary" onClick={() => confirmEnable(code)}>
              {lang.handle("Confirmar")}
            </Button>
            <Button onClick={() => { setSetupData(null); setCode(""); }}>
              {lang.handle("Cancelar")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default withLocale(TwoFactorCard);
